import { isNullOrUndefined } from "base/utils";
import { RecipeContext } from "components/recipe/providers/RecipeProvider";
import { useAuthentication } from "authentication/AuthenticationContext";
import { useNavigate } from "react-router-dom";
import AddRecipeBaseInfo from "components/recipe/AddRecipeBaseInfo";
import AddRecipeIngredients from "components/recipe/AddRecipeIngredients";
import AddRecipeInstruction from "components/recipe/AddRecipeInstruction";
import AddRecipePrepWork from "components/recipe/AddRecipePrepWork";
import BaseAccordionList from "base/BaseAccordionList";
import BaseButton from "base/BaseButton";
import React, { useContext, useEffect, useRef, useState } from "react";
import RecipeFilterByBadge from "components/recipe/RecipeFilterByBadge";

const AddRecipe = ({ closeModal }) => {
  const [expandedItem, setExpandedItem] = useState("");
  const [isBaseInfoValid, setIsBaseInfoValid] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recipeInstructions, setRecipeInstructions] = useState([]);
  const { badges, saveRecipe } = useContext(RecipeContext);
  const { user } = useAuthentication();
  const closeTimerRef = useRef(null);
  const isSubmittingRef = useRef(false);
  const navigate = useNavigate();
  const [validationWarnings, setValidationWarnings] = useState({
    baseInfo: null,
    Ingredients: null,
    Instructions: null,
  });
  const validationWarningsRef = useRef(validationWarnings);
  const [recipe, setRecipe] = useState({
    additional_pictures: [],
    badge_ids: [],
    calories: null,
    cook_time: null,
    ingredients: [],
    instructions: [],
    is_private: false,
    kitchen_tools: [],
    macros: [],
    main_picture: null,
    num_of_servings: null,
    preheat_temp: null,
    prep_time: null,
    recipe_name: "",
    serving_size: "",
    total_time: null,
  });
  const isRecipeValid =
    isBaseInfoValid &&
    recipe.ingredients.length > 0 &&
    recipe.instructions.length > 0;

  const newInstruction = {};

  const addRecipe = async () => {
    if (isSubmittingRef.current) return; // ignore extra clicks
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      await saveRecipe(recipe);
      setIsSaving(true);
      closeTimerRef.current = setTimeout(closeModal, 5000);
    } catch (err) {
      console.error(err.message);
      // let them try again if the save failed
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleExpandItem = (clickedItem) => {
    if (expandedItem != clickedItem) setExpandedItem(clickedItem);
    else setExpandedItem("");
  };

  useEffect(() => {
    //setRecipeIngredients([newIngredient]);
    setRecipeInstructions([newInstruction]);
  }, []);

  useEffect(() => {
    validationWarningsRef.current = validationWarnings;
  }, [validationWarnings]);

  const updateRecipe = (field, val) => {
    if (field == "images") {
      //set main recipe url, and save all pictures to additional pics
      setRecipe((prev) => ({
        ...prev,
        main_picture: val[0]?.url,
        additional_pictures: val,
      }));
    } else {
      //in the case of prep and cook times, it's sent thru as objects, so set object
      if (typeof field === "object") {
        setRecipe((prev) => ({ ...prev, ...field }));
      } else if (field == "badge_ids") {
        setRecipe((prev) => ({ ...prev, [field]: [...val] }));
      } else {
        //all others come through with the field and string/ number/ array value
        setRecipe((prev) => ({ ...prev, [field]: val }));
      }
    }
    if (field == "ingredients" || field == "instructions") {
      updateRecipeValidation(
        field,
        val?.length > 0 ? true : "Must have at least one",
        field == "ingredients" ? "Ingredients" : "Instructions",
      );
    }
  };

  const handleCancel = () => {
    closeModal();
  };

  const updateRecipeValidation = (field, isValidStatus, accordionItem) => {
    const updatedWarnings = {
      ...validationWarningsRef.current,
      [accordionItem]: {
        ...validationWarningsRef.current[accordionItem],
        [field]: isValidStatus,
      },
    };

    validationWarningsRef.current = updatedWarnings;
    setValidationWarnings(updatedWarnings);

  const baseInfoWarnings = updatedWarnings.baseInfo;
    setIsBaseInfoValid(
      !isNullOrUndefined(baseInfoWarnings)
        ? Object.values(baseInfoWarnings).every((v) => typeof v !== "string")
        : false,
    );
  };

  const handleLogin = () => {
    clearTimeout(closeTimerRef.current); // cancel the auto-close
    navigate("/admin");
    closeModal();
  };

  useEffect(() => {
    // if the modal closes, don't leave the timer running
    return () => clearTimeout(closeTimerRef.current);
  }, []);

  return (
    <section className={isSaving ? "Is-Saving" : ""}>
      {isSaving ? (
        //if the user is already logged in, let them know they will be the only one to see their recipe
        //until it is approved by an admin (provided it is not a private recipe)
        user ? (
          <p>
            Your recipe will be available publicly when approved, if it is not
            set to Private.
          </p>
        ) : (
          //if the user is not signed in, then give them the option to see it immediately/ claim as their own
          <>
            <p className="mt-10">
              Your Recipe will be available, once it is approved by an
              Administrator
            </p>
            <div>
              <h6>
                To see your recipe immediately and add it to your recipe
                library, please sign in.
              </h6>
              <div className="Justify-Center my-10">
                <BaseButton
                  buttonText="Log in"
                  isInverse
                  widthClass="w-32"
                  handleClick={handleLogin}
                />
              </div>
            </div>
          </>
        )
      ) : (
        <>
          <div className="w-100">
            {/* <BaseProgressBar steps={[1, 2, 3, 4, 5]} /> */}
            <section>
              <BaseAccordionList
                accordionFunctions={[
                  {
                    name: "Recipe Details",
                    Details: [
                      {
                        component: (
                          <AddRecipeBaseInfo
                            onRecipeUpdate={updateRecipe}
                            recipe={recipe}
                            validationMessages={updateRecipeValidation}
                          />
                        ),
                        isValid: isBaseInfoValid,
                      },
                    ],
                  },
                  {
                    name: "Badges",
                    Badges: [
                      {
                        component: (
                          <RecipeFilterByBadge
                            badges={badges}
                            defaultAllChecked={false}
                            isFilter={false}
                            onRecipeUpdate={updateRecipe}
                            recipe={recipe}
                          />
                        ),
                        isValid: true,
                      },
                    ],
                  },
                  {
                    name: "Prep Work",
                    PrepWork: [
                      {
                        component: (
                          <AddRecipePrepWork onRecipeUpdate={updateRecipe} />
                        ),
                        isValid: true,
                      },
                    ],
                  },
                  {
                    name: "Ingredients",
                    Ingredients: [
                      {
                        component: (
                          <AddRecipeIngredients onRecipeUpdate={updateRecipe} />
                        ),
                        isValid: true,
                      },
                    ],
                  },
                  {
                    name: "Instructions",
                    Instructions: [
                      {
                        component: (
                          <AddRecipeInstruction onRecipeUpdate={updateRecipe} />
                        ),
                        isValid: true,
                      },
                    ],
                  },
                ]}
                expandedItem={expandedItem}
                onExpandItem={handleExpandItem}
                accordionClasses="Add-Recipe-Accordion"
              />
            </section>
            {user && (
              <label className="recipe-privacy-option">
                <input
                  type="checkbox"
                  checked={recipe.is_private}
                  onChange={(event) =>
                    updateRecipe("is_private", event.target.checked)
                  }
                />
                <span>Private recipe — only you can view it</span>
              </label>
            )}

            <div className="Cancel-Add-Buttons">
              <BaseButton
                buttonText="Cancel"
                handleClick={handleCancel}
                isInverse
                widthClass="w-32"
              />
              {/* <BaseButton buttonText="Preview" isInverse widthClass="w-30" /> */}
              <BaseButton
                buttonText={isSubmitting ? "Saving..." : "Add Recipe"}
                isDisabled={!isRecipeValid || isSubmitting}
                isInverse
                widthClass="w-32"
                handleClick={addRecipe}
              />
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default AddRecipe;
